import { useParams, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useOrder, useOrderMessages, sendOrderMessage, sendGuestOrderMessage, submitOrderUtr, useCheckoutSettings, lookupOrderForTracking } from "@/lib/firestoreService";
import { fulfillmentMeta, isCourierOrder, isPickupOrder } from "@/lib/fulfillment";
import { OrderTimeline } from "@/components/OrderTimeline";
import { formatPrice, WHATSAPP_NUMBER } from "@/lib/cart";
import { MessageCircle, ArrowLeft, MapPin, Truck, Send, Loader2, Receipt, Package, Gift } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { firebaseReady } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { findStatus } from "@/lib/seedData";
import { toast } from "sonner";
import { useSEO } from "@/lib/useSEO";
import { track } from "@/lib/analytics";
import { orderNumber } from "@/lib/orderNumber";

const UTR_EDIT_WINDOW_MS = 5 * 60 * 1000;

function statusBadgeClass(key) {
  if (key === "cancelled")              return "bg-red-50 text-red-700 border-red-200";
  if (key === "delivered")              return "bg-[#556B2F]/15 text-[#556B2F] border-[#556B2F]/30";
  if (key === "dispatched")             return "bg-blue-50 text-blue-700 border-blue-200";
  if (["under_preparation","quality_check","ready_dispatch"].includes(key))
                                        return "bg-orange-50 text-orange-700 border-orange-200";
  return "bg-[#5A2D1D]/10 text-[#5A2D1D] border-[#5A2D1D]/25";
}

function fmtDateTime(seconds) {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export const TrackOrderPage = () => {
  useSEO({ title: "Track Your Order", description: "Track your Annapurna Home Pickles order status and delivery." });
  const { id } = useParams();
  const { t, isTe } = useLang();
  const { user } = useAuth();
  const settings = useCheckoutSettings();
  const [lookupRef, setLookupRef] = useState(id || "");
  const [lookupContact, setLookupContact] = useState("");
  const [lookupOrder, setLookupOrder] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const directMode = Boolean(user && id);
  const directOrder = useOrder(directMode ? id : null);
  const order = directMode ? directOrder : lookupOrder;
  const fMeta = order ? fulfillmentMeta(order, isTe) : null;

  useEffect(() => {
    if (!directMode && id) setLookupRef(id);
  }, [directMode, id]);

  if (!firebaseReady) {
    return (
      <Centered>
        <div className="font-display text-2xl text-[#5A2D1D] mb-2">{t.tracking.tracking_unavailable}</div>
        <p className="text-[#2E1A12]/80 text-sm">{t.tracking.firebase_not_connected}</p>
      </Centered>
    );
  }

  if (!directMode && !order) {
    return (
      <Centered>
        <div className="font-display text-2xl text-[#5A2D1D] mb-2">Track Your Order</div>
        <p className="text-[#2E1A12]/80 text-sm mb-5">
          Enter your Order ID and Email/Mobile to view order details.
        </p>
        <div className="space-y-3 text-left">
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">
              Order ID
            </label>
            <input
              value={lookupRef}
              onChange={(e) => setLookupRef(e.target.value)}
              placeholder="e.g. AHP2802..., #6DCC..., or full UUID"
              className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">
              Email or Mobile
            </label>
            <input
              value={lookupContact}
              onChange={(e) => setLookupContact(e.target.value)}
              placeholder="Enter the contact used in checkout"
              className="w-full px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
            />
          </div>
          {lookupError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
              {lookupError}
            </div>
          )}
          <button
            onClick={async () => {
              if (!lookupRef.trim() || !lookupContact.trim()) {
                setLookupError("Please enter both Order ID and Email/Mobile.");
                return;
              }
              setLookupLoading(true);
              setLookupError("");
              try {
                const found = await lookupOrderForTracking({
                  orderRef: lookupRef.trim(),
                  contact: lookupContact.trim(),
                });
                if (!found) {
                  setLookupOrder(null);
                  setLookupError("Order not found for the given details.");
                  return;
                }
                setLookupOrder(found);
                track("order_tracking_lookup_success", { order_id: found.id });
              } catch (e) {
                setLookupOrder(null);
                setLookupError(e.message || "Could not verify details.");
              } finally {
                setLookupLoading(false);
              }
            }}
            disabled={lookupLoading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] py-2.5 rounded-sm font-semibold"
          >
            {lookupLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            View Order
          </button>
        </div>
      </Centered>
    );
  }

  if (directMode && order === undefined) {
    return (
      <div className="min-h-screen bg-[#F5E6CC] py-12">
        <div className="max-w-4xl mx-auto px-4 space-y-4">
          <div className="h-32 bg-[#EFD9B4]/60 rounded-sm animate-pulse" />
          <div className="h-64 bg-[#EFD9B4]/60 rounded-sm animate-pulse" />
        </div>
      </div>
    );
  }

  if (directMode && order === null) {
    return (
      <Centered>
        <div className="font-display text-2xl text-[#5A2D1D] mb-2" data-testid="order-not-found">
          {t.tracking.not_found}
        </div>
        <Link to="/my-orders" className="text-[#556B2F] font-semibold hover:underline mt-2 inline-block">
          ← {t.orders.my_orders}
        </Link>
      </Centered>
    );
  }

  const st = findStatus(order.status);
  const statusLabel = isTe ? st?.label_te : st?.label;
  const waNote = `Hello, I have a query about Order ${orderNumber(order)}.`;
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waNote)}`;

  // Price breakdown
  const hasBreakdown = order.gst_amount != null;
  const subtotal       = order.subtotal        ?? order.total ?? 0;
  const gstAmount      = order.gst_amount      ?? 0;
  const gstPercent     = order.gst_percent     ?? 5;
  const deliveryCharge = order.delivery_charge ?? 0;
  const grandTotal     = order.total           ?? 0;

  return (
    <div data-testid="track-order-page" className="min-h-screen bg-[#F5E6CC]">

      {/* Page Header — compact */}
      <div className="border-b border-[#5A2D1D]/15 bg-[#EFD9B4]/50 py-3 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={directMode ? "/my-orders" : "/track-order"}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#556B2F] hover:underline flex-shrink-0"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {directMode ? t.orders.my_orders : t.tracking.back}
              </Link>
              <span className="text-[#5A2D1D]/30 select-none">|</span>
              <div className="min-w-0">
                <h1 className="font-display text-base sm:text-lg font-bold text-[#5A2D1D] leading-tight" data-testid="order-id">
                  {t.tracking.order_hash} {orderNumber(order)}
                </h1>
                <div className="text-xs text-[#2E1A12]/60">
                  {t.tracking.placed_on} {fmtDateTime(order.createdAt?.seconds)}
                </div>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border flex-shrink-0 ${statusBadgeClass(order.status)}`}>
              <span>{st?.icon}</span> {statusLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {fMeta && (
          <div className={`mb-4 p-3 rounded-sm border text-sm ${fMeta.badgeClass}`}>
            <span className="font-bold">{fMeta.icon} {fMeta.label}</span>
            {isPickupOrder(order) && settings.pickupAddress?.note && (
              <p className="text-xs mt-1 opacity-80">{settings.pickupAddress.note}</p>
            )}
            {isCourierOrder(order) && (
              <p className="text-xs mt-1 opacity-80">{settings.courierNote || "Shipped via courier — not instant delivery."}</p>
            )}
          </div>
        )}
        {order.payment_mode === "phonepe" ? null : directMode ? (
          <UtrSection order={order} />
        ) : (
          <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4 mb-4 text-xs text-[#2E1A12]/70">
            For security, UTR edits and message replies are available only from signed-in order views.
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-4">

          {/* LEFT — Timeline */}
          <div className="lg:col-span-3">
            <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4">
              <h2 className="font-display text-sm font-bold text-[#5A2D1D] mb-4 flex items-center gap-1.5">
                <Truck className="w-4 h-4" /> {t.tracking.timeline}
              </h2>
              <OrderTimeline order={order} />
            </div>
          </div>

          {/* RIGHT — Details */}
          <div className="lg:col-span-2 space-y-4">

            {/* Order Items + Price Breakdown */}
            <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4">
              <h3 className="font-display text-sm font-bold text-[#5A2D1D] mb-3">{t.tracking.order_items}</h3>
              <ul className="divide-y divide-[#5A2D1D]/10 mb-3">
                {order.items?.map((it, idx) => (
                  <li key={idx} className="py-2 flex gap-2.5 items-center" data-testid={`order-item-${idx}`}>
                    {it.image_url ? (
                      <img src={it.image_url} alt={it.name} loading="lazy" decoding="async" className="w-10 h-10 object-cover rounded-sm flex-shrink-0 border border-[#5A2D1D]/10" />
                    ) : (
                      <div className="w-10 h-10 rounded-sm flex-shrink-0 border border-[#5A2D1D]/10 bg-[#556B2F]/10 text-[#556B2F] grid place-items-center">
                        {it.item_type === "sample_pack" ? <Gift className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#5A2D1D] truncate">{it.name}</div>
                      <div className="text-xs text-[#556B2F] font-bold uppercase tracking-widest">
                        {it.size} × {it.quantity}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-[#5A2D1D] whitespace-nowrap">
                      {formatPrice(it.price * it.quantity)}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Price breakdown */}
              <div className="border-t border-[#5A2D1D]/15 pt-3 space-y-1.5 text-sm">
                {hasBreakdown ? (
                  <>
                    <PriceLine label={t.tracking.items_subtotal} value={formatPrice(subtotal)} />
                    {order.offer_code && (
                      <div className="flex justify-between text-[#556B2F] font-semibold">
                        <span>Discount ({order.offer_code})</span>
                        <span>-{formatPrice(order.discount_amount || 0)}</span>
                      </div>
                    )}
                    <PriceLine
                      label={`GST (${gstPercent}%)`}
                      value={formatPrice(gstAmount)}
                    />
                    {isCourierOrder(order) && (
                    <PriceLine
                      label={t.tracking.courier_delivery}
                      value={deliveryCharge === 0 ? t.tracking.free : formatPrice(deliveryCharge)}
                      muted={deliveryCharge === 0}
                    />
                    )}
                    <div className="flex justify-between pt-2 border-t border-[#5A2D1D]/20 font-display font-bold text-[#5A2D1D]">
                      <span>{t.tracking.grand_total}</span>
                      <span data-testid="order-total">{formatPrice(grandTotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between font-display font-bold text-[#5A2D1D] text-sm">
                    <span>{t.tracking.total}</span>
                    <span data-testid="order-total">{formatPrice(grandTotal)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Address */}
            <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4">
              <h3 className="font-display text-sm font-bold text-[#5A2D1D] mb-2 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#556B2F]" /> {isPickupOrder(order) ? t.tracking.pickup_address : t.tracking.delivery_address}
              </h3>
              <div className="text-sm text-[#2E1A12]/90 space-y-0.5">
                <div className="font-bold text-[#5A2D1D]">{order.customer_name}</div>
                <div>{order.phone}</div>
                <div className="mt-1 whitespace-pre-wrap text-[#2E1A12]/80">{order.address}</div>
                {(order.city || order.state || order.pincode) && (
                  <div className="text-[#2E1A12]/80">
                    {[order.city, order.state, order.pincode].filter(Boolean).join(", ")}
                  </div>
                )}
                {order.country && <div className="text-[#2E1A12]/80">{order.country}</div>}
                {order.notes && (
                  <div className="mt-2 italic text-[#2E1A12]/60 text-xs">{t.tracking.note_label}: {order.notes}</div>
                )}
              </div>
            </div>

            {/* Courier Info */}
            {order.courier?.name && (
              <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4">
                <h3 className="font-display text-sm font-bold text-[#5A2D1D] mb-2 flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-[#556B2F]" /> {t.tracking.courier}
                </h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#2E1A12]/70">{t.tracking.courier_label}</span>
                    <span className="font-bold text-[#5A2D1D]" data-testid="order-courier-name">{order.courier.name}</span>
                  </div>
                  {order.courier.trackingId && (
                    <div className="flex justify-between">
                      <span className="text-[#2E1A12]/70">{t.tracking.tracking_id}</span>
                      <span className="font-bold text-[#5A2D1D]" data-testid="order-tracking-id">{order.courier.trackingId}</span>
                    </div>
                  )}
                </div>
                {order.courier.trackingUrl && (
                  <a
                    href={order.courier.trackingUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="order-courier-link"
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F] hover:text-[#F5E6CC] py-1.5 rounded-full font-bold text-xs transition-colors"
                  >
                    {t.tracking.track_courier} →
                  </a>
                )}
              </div>
            )}

            {/* Contact Support */}
            <a
              data-testid="order-whatsapp-button"
              href={waLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => track("whatsapp_contact_clicked", { source: "track_order_page", order_id: order.id })}
              className="w-full inline-flex items-center justify-center gap-1.5 border border-[#5A2D1D]/30 hover:border-[#5A2D1D] text-[#5A2D1D] py-2 rounded-full font-bold text-xs transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" /> {t.tracking.contact_whatsapp}
            </a>
          </div>
        </div>

        {/* Messages Thread */}
        {directMode && (
          <OrderMessages orderId={order.id} status={order.status} user={user} customerName={order.customer_name} isGuestOrder={!order.uid} t={t} />
        )}
      </div>
    </div>
  );
};

const OrderMessages = ({ orderId, status, user, customerName, isGuestOrder, t }) => {
  const messages = useOrderMessages(orderId);
  const settings = useCheckoutSettings();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(Date.now());
  const threadRef = useRef(null);
  const localLastSentRef = useRef(0);

  const delivered = status === "delivered";

  // Scroll only the message thread's own internal container, not the page —
  // scrollIntoView() would drag the whole page down to reveal this section
  // every time the messages list changes, including on first page load.
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  const lastCustomerMsg = [...messages].reverse().find((m) => m.from === "customer");
  const lastSentAtMs = Math.max(
    lastCustomerMsg?.at?.seconds ? lastCustomerMsg.at.seconds * 1000 : 0,
    localLastSentRef.current
  );
  const cooldownMs = Math.max(0, (settings.messageCooldownMinutes || 0) * 60 * 1000);
  const cooldownUntilMs = lastSentAtMs ? lastSentAtMs + cooldownMs : 0;
  const inCooldown = cooldownUntilMs > now;
  const remainingSec = Math.max(0, Math.ceil((cooldownUntilMs - now) / 1000));
  const remainingHrs = Math.floor(remainingSec / 3600);
  const remainingLabel = remainingHrs > 0
    ? `${remainingHrs}:${String(Math.floor((remainingSec % 3600) / 60)).padStart(2, "0")}:${String(remainingSec % 60).padStart(2, "0")}`
    : `${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, "0")}`;

  useEffect(() => {
    if (!lastSentAtMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [lastSentAtMs]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      if (isGuestOrder) {
        await sendGuestOrderMessage(orderId, {
          sender: customerName || "Guest",
          text,
        });
      } else {
        if (!user) { toast.error(t.tracking.msg_signin_toast); return; }
        await sendOrderMessage(orderId, {
          from: "customer",
          sender: customerName || user.user_metadata?.name || user.email,
          text,
        });
      }
      track("order_message_sent", { order_id: orderId, is_guest: isGuestOrder });
      localLastSentRef.current = Date.now();
      setNow(Date.now());
      setText("");
    } catch (e) {
      toast.error(e.message || t.tracking.msg_error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-4 bg-white/60 border border-[#5A2D1D]/15 rounded-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#5A2D1D]/10">
        <MessageCircle className="w-3.5 h-3.5 text-[#556B2F]" />
        <h3 className="font-display text-sm font-bold text-[#5A2D1D]">{t.tracking.messages}</h3>
        <span className="hidden sm:inline text-[10px] text-[#2E1A12]/40">Typically resolution provided in 24hrs</span>
        {messages.length > 0 && (
          <span className="ml-auto text-xs text-[#2E1A12]/50">{messages.length}</span>
        )}
      </div>

      {/* Thread */}
      <div ref={threadRef} className="px-4 py-3 space-y-2 max-h-52 overflow-y-auto">
        {messages.length === 0 ? (
          <p className="text-sm text-[#2E1A12]/50 text-center py-4">
            {t.tracking.no_messages}
          </p>
        ) : (
          messages.map((m) => {
            const isAdmin = m.from === "admin";
            return (
              <div key={m.id} className={`flex gap-3 ${isAdmin ? "" : "flex-row-reverse"}`}>
                <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold flex-shrink-0 ${
                  isAdmin ? "bg-[#5A2D1D] text-[#F5E6CC]" : "bg-[#556B2F] text-[#F5E6CC]"
                }`}>
                  {isAdmin ? "A" : "U"}
                </div>
                <div className={`max-w-[75%] ${isAdmin ? "" : "items-end"} flex flex-col gap-0.5`}>
                  <div className={`text-[10px] font-bold uppercase tracking-widest ${
                    isAdmin ? "text-[#5A2D1D]" : "text-[#556B2F] text-right"
                  }`}>
                    {isAdmin ? "Annapurna Team" : m.sender}
                  </div>
                  <div className={`text-sm px-3 py-2 rounded-sm leading-relaxed ${
                    isAdmin
                      ? "bg-[#EFD9B4] text-[#2E1A12] rounded-tl-none"
                      : "bg-[#5A2D1D]/10 text-[#2E1A12] rounded-tr-none"
                  }`}>
                    {m.text}
                  </div>
                  {m.at?.seconds && (
                    <div className={`text-[10px] text-[#2E1A12]/40 ${isAdmin ? "" : "text-right"}`}>
                      {new Date(m.at.seconds * 1000).toLocaleString("en-IN", {
                        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Reply box */}
      {delivered ? (
        <div className="border-t border-[#5A2D1D]/10 px-4 py-3">
          <p className="text-sm text-[#2E1A12]/50">This order has been delivered — messaging is now closed.</p>
        </div>
      ) : (
        <div className="border-t border-[#5A2D1D]/10 px-4 py-3 flex gap-2">
          {(isGuestOrder || user) ? (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t.tracking.message_placeholder}
                rows={2}
                disabled={inCooldown}
                className="flex-1 px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/25 focus:border-[#5A2D1D] focus:outline-none text-sm resize-none disabled:opacity-50"
              />
              <button
                onClick={send}
                disabled={sending || !text.trim() || inCooldown}
                title={inCooldown ? `You can send another message in ${remainingLabel}` : undefined}
                className="self-end px-4 py-2.5 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 text-[#F5E6CC] rounded-sm font-bold transition-colors whitespace-nowrap"
              >
                {inCooldown ? remainingLabel : <Send className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <p className="text-sm text-[#2E1A12]/60 py-1">{t.tracking.sign_in_to_msg}</p>
          )}
        </div>
      )}
    </div>
  );
};

const UtrSection = ({ order }) => {
  // Local state, not just the order prop directly: logged-in owners/admins
  // get this updated live via realtime, but guests don't (by design — see
  // useOrder), so a guest's own submit must update the display itself from
  // the RPC's response rather than waiting for a push that'll never arrive.
  const [utr, setUtr] = useState(order?.utr || "");
  const [submittedAtMs, setSubmittedAtMs] = useState(
    order?.utrSubmittedAt?.seconds ? order.utrSubmittedAt.seconds * 1000 : null
  );
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    setUtr(order?.utr || "");
    setSubmittedAtMs(order?.utrSubmittedAt?.seconds ? order.utrSubmittedAt.seconds * 1000 : null);
  }, [order?.utr, order?.utrSubmittedAt?.seconds]);

  useEffect(() => {
    if (!submittedAtMs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [submittedAtMs]);

  if (!order) return null;

  const elapsed = submittedAtMs ? now - submittedAtMs : null;
  const canEdit = !submittedAtMs || elapsed < UTR_EDIT_WINDOW_MS;
  const remainingMs = submittedAtMs ? Math.max(0, UTR_EDIT_WINDOW_MS - elapsed) : 0;
  const remainingLabel = `${Math.floor(remainingMs / 60000)}:${String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, "0")}`;

  const submit = async () => {
    if (!value.trim()) { toast.error("Please enter a UTR number."); return; }
    setSaving(true);
    try {
      const updated = await submitOrderUtr(order.id, value.trim());
      track("utr_submitted", { order_id: order.id, utr_value: value.trim(), is_edit: !!utr });
      if (updated) {
        setUtr(updated.utr || "");
        setSubmittedAtMs(updated.utrSubmittedAt?.seconds ? updated.utrSubmittedAt.seconds * 1000 : null);
      }
      toast.success("UTR saved");
      setEditing(false);
      setValue("");
    } catch (e) {
      toast.error(e.message || "Could not save UTR");
    } finally {
      setSaving(false);
    }
  };

  if (!utr || editing) {
    return (
      <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4 mb-4">
        <h3 className="font-display text-sm font-bold text-[#5A2D1D] mb-2 flex items-center gap-1.5">
          <Receipt className="w-3.5 h-3.5 text-[#556B2F]" /> Payment UTR / Reference Number
        </h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="utr-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter UTR number"
            className="flex-1 px-3 py-2 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm"
          />
          <div className="flex gap-2">
            <button
              data-testid="utr-submit"
              onClick={submit}
              disabled={saving}
              className="px-4 py-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] rounded-sm font-semibold text-sm disabled:opacity-60 inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Submit
            </button>
            {editing && (
              <button
                onClick={() => { setEditing(false); setValue(""); }}
                className="px-3 py-2 text-sm text-[#5A2D1D]/60 hover:text-[#5A2D1D]"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
        <p className="mt-2 text-[10px] text-[#7A3B25] font-semibold">UTR is mandatory to get your order confirmed.</p>
      </div>
    );
  }

  return (
    <div className="bg-white/60 border border-[#5A2D1D]/15 rounded-sm p-4 mb-4 flex items-center justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F]">UTR / Reference Number</div>
        <div className="font-bold text-[#5A2D1D]" data-testid="utr-value">{utr}</div>
      </div>
      {canEdit ? (
        <button
          onClick={() => { setEditing(true); setValue(utr); track("utr_edit_started", { order_id: order.id }); }}
          className="flex-shrink-0 text-xs font-bold text-[#556B2F] border border-[#556B2F]/40 px-3 py-1.5 rounded-full hover:bg-[#556B2F]/10"
        >
          Edit ({remainingLabel})
        </button>
      ) : (
        <span className="flex-shrink-0 text-xs text-[#2E1A12]/40">Locked</span>
      )}
    </div>
  );
};

const PriceLine = ({ label, value, muted }) => (
  <div className="flex justify-between text-[#2E1A12]">
    <span className="text-[#2E1A12]/70">{label}</span>
    <span className={`font-semibold ${muted ? "text-[#556B2F]" : "text-[#5A2D1D]"}`}>{value}</span>
  </div>
);

const Centered = ({ children }) => (
  <div className="min-h-[60vh] grid place-items-center px-4 bg-[#F5E6CC]">
    <div className="bg-white/60 border border-[#5A2D1D]/25 rounded-sm p-10 max-w-md w-full text-center">
      {children}
    </div>
  </div>
);
